// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/cofre/ReformaPainel.tsx
//  Reforma Tributária de um contribuinte a partir do Cofre Fiscal: conferência do IBS/CBS
//  destacado nas saídas da competência (o ano-teste de 2026), créditos nas compras por
//  regime do fornecedor e a transição 2026–2033 — ou, para o Simples, o peso das vendas a
//  empresas na decisão pelo regime híbrido. Usado no Portal do Contador e na Central Fiscal.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Info, Landmark, Loader2, Scale } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from '@/lib/utils';
import { percentual } from './faturamento';
import type { ParametrosReforma, ReformaConferencia, ReformaSimulacao } from './reforma';

interface Props {
  /** Chave de cache (inclua quem é o contribuinte). */
  chave: unknown[];
  competencia: number;
  conferir: (competencia: number) => Promise<ReformaConferencia>;
  simular: (competencia: number, p: ParametrosReforma) => Promise<ReformaSimulacao>;
}

export function ReformaPainel({ chave, competencia, conferir, simular }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: [...chave, 'reforma', competencia],
    queryFn: () => conferir(competencia),
    retry: false,
  });

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-40" /><Skeleton className="h-64" /></div>;
  if (error || !data)
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>{error instanceof Error ? error.message : 'Conferência da Reforma indisponível.'}</AlertDescription>
      </Alert>
    );

  return (
    <div className="space-y-6">
      <Conferencia c={data} />
      <Creditos c={data} />
      <Simulacao chave={chave} competencia={competencia} simular={simular} />
    </div>
  );
}

// ── Conferência do IBS/CBS ───────────────────────────────────────────────────

function Conferencia({ c }: { c: ReformaConferencia }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-start gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Landmark className="h-4 w-4" />IBS e CBS nas notas de saída</CardTitle>
            <CardDescription>NF-e e NFC-e autorizadas da competência.</CardDescription>
          </div>
          {c.obrigado && <Badge variant="outline" className="sm:ml-auto border-primary text-primary">Destaque obrigatório</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert><Info className="h-4 w-4" /><AlertDescription>{c.orientacao}</AlertDescription></Alert>
        {c.documentosALer > 0 && (
          <p className="text-xs text-muted-foreground">
            {c.documentosALer} documento(s) desta competência ainda estão sendo relidos para a conferência de alíquota e cálculo.
          </p>
        )}
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <Kpi titulo="Itens com o grupo IBS/CBS" valor={`${c.itensComGrupo} de ${c.itensSaida}`} detalhe={`${percentual(c.cobertura)} dos itens`}
            alerta={c.obrigado && c.itensComGrupo < c.itensSaida} />
          <Kpi titulo="Base do IBS/CBS" valor={formatCurrency(c.base)} />
          <Kpi titulo="CBS destacada" valor={formatCurrency(c.cbs)} />
          <Kpi titulo="IBS destacado" valor={formatCurrency(c.ibs)} />
        </div>

        <div className="space-y-3">
          {c.alertas.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              {c.itensComGrupo > 0 ? 'Nenhuma inconsistência no IBS/CBS destacado nesta competência.' : 'Nenhuma inconsistência a apontar nesta competência.'}
            </p>
          ) : c.alertas.map((a) => (
            <Alert key={a.tipo}>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className="flex items-center gap-2">{a.titulo} <Badge variant="secondary">{a.quantidade}</Badge></AlertTitle>
              <AlertDescription className="space-y-1">
                <p>{a.orientacao}</p>
                <ul className="text-xs text-muted-foreground list-disc pl-4">
                  {a.exemplos.map((e, i) => <li key={i}>Nota {e.numero}: {e.detalhe}</li>)}
                  {a.quantidade > a.exemplos.length && <li>e mais {a.quantidade - a.exemplos.length}…</li>}
                </ul>
              </AlertDescription>
            </Alert>
          ))}
        </div>

        {c.porClassificacao.length > 0 && (
          <div className="overflow-x-auto">
            <p className="text-sm font-medium mb-2">Por CST e classificação tributária (cClassTrib)</p>
            <Table>
              <TableHeader><TableRow>
                <TableHead>CST</TableHead><TableHead>cClassTrib</TableHead><TableHead className="text-right">Itens</TableHead>
                <TableHead className="text-right">Valor</TableHead><TableHead className="text-right">Base</TableHead>
                <TableHead className="text-right">CBS</TableHead><TableHead className="text-right">IBS</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {c.porClassificacao.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono">{l.cst ?? '—'}</TableCell><TableCell className="font-mono">{l.classTrib ?? '—'}</TableCell>
                    <TableCell className="text-right">{l.itens.toLocaleString('pt-BR')}</TableCell>
                    <Din v={l.valor} /><Din v={l.base} /><Din v={l.cbs} /><Din v={l.ibs} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Creditos({ c }: { c: ReformaConferencia }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">IBS e CBS nas compras</CardTitle>
        <CardDescription>
          O que os fornecedores destacaram na competência. A partir de 2027 isso vira crédito — cheio só de quem é do regime normal.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        {c.creditos.length === 0 ? <p className="px-6 pb-6 text-sm text-muted-foreground">Nenhuma compra no cofre nesta competência.</p> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Fornecedores</TableHead><TableHead className="text-right">Quantos</TableHead>
              <TableHead className="text-right">Compras</TableHead><TableHead className="text-right">CBS</TableHead><TableHead className="text-right">IBS</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {c.creditos.map((x) => (
                <TableRow key={x.chave}>
                  <TableCell>{x.rotulo}</TableCell><TableCell className="text-right">{x.fornecedores}</TableCell>
                  <Din v={x.compras} /><Din v={x.cbs} /><Din v={x.ibs} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ── Transição 2026–2033 ──────────────────────────────────────────────────────

function Simulacao({ chave, competencia, simular }: Pick<Props, 'chave' | 'competencia' | 'simular'>) {
  const [cbs, setCbs] = useState('');
  const [ibs, setIbs] = useState('');
  const [parametros, setParametros] = useState<ParametrosReforma>({});
  const numero = (s: string) => (s.trim() ? Number(s.replace(',', '.')) || undefined : undefined);

  const { data, isFetching, error } = useQuery({
    queryKey: [...chave, 'reforma-simulacao', competencia, parametros],
    queryFn: () => simular(competencia, parametros),
    retry: false,
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Scale className="h-4 w-4" />Transição até 2033</CardTitle>
        <CardDescription>
          A carga dos últimos 12 meses em cada ano da Reforma, com as alíquotas de referência. Estimativa para orientar a conversa, não para apurar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form className="grid gap-3 sm:grid-cols-3 items-end"
          onSubmit={(e) => { e.preventDefault(); setParametros({ cbs: numero(cbs), ibs: numero(ibs) }); }}>
          <div className="space-y-1.5">
            <Label htmlFor="ref-cbs">CBS de referência (%)</Label>
            <Input id="ref-cbs" inputMode="decimal" placeholder={data ? String(data.cbsReferencia).replace('.', ',') : '8,8'} value={cbs} onChange={(e) => setCbs(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ref-ibs">IBS de referência (%)</Label>
            <Input id="ref-ibs" inputMode="decimal" placeholder={data ? String(data.ibsReferencia).replace('.', ',') : '17,7'} value={ibs} onChange={(e) => setIbs(e.target.value)} />
          </div>
          <Button type="submit" disabled={isFetching}>{isFetching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Recalcular</Button>
        </form>

        {error && <Alert><Info className="h-4 w-4" /><AlertDescription>{(error as Error).message}</AlertDescription></Alert>}
        {!data ? <Skeleton className="h-48" /> : (
          <>
            <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
              <Kpi titulo="Vendas em 12 meses" valor={formatCurrency(data.receita)} />
              <Kpi titulo="Base sem os tributos de hoje" valor={formatCurrency(data.baseSaidas)} />
              <Kpi titulo="Compras do regime normal" valor={formatCurrency(data.comprasRegimeNormal)} detalhe="geram crédito cheio" />
              <Kpi titulo="Compras do Simples" valor={formatCurrency(data.comprasSimples)} detalhe="crédito reduzido" />
            </div>

            {data.simples && data.hibrido ? (
              <Alert>
                <Scale className="h-4 w-4" />
                <AlertTitle>Simples puro ou regime híbrido a partir de 2027?</AlertTitle>
                <AlertDescription className="space-y-1">
                  <p>
                    {percentual(data.hibrido.percentual)} das vendas ({formatCurrency(data.hibrido.receitaEmpresas)}) vão para empresas.
                    No regime híbrido, esses clientes aproveitariam cerca de {formatCurrency(data.hibrido.creditoClientesHibrido)} por ano de crédito de IBS/CBS;
                    no Simples puro, só a parte que o DAS embute.
                  </p>
                  <p className="font-medium">{data.hibrido.orientacao}</p>
                </AlertDescription>
              </Alert>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Ano</TableHead><TableHead className="text-right">ICMS/ISS</TableHead><TableHead className="text-right">PIS/COFINS</TableHead>
                    <TableHead className="text-right">IPI</TableHead><TableHead className="text-right">CBS</TableHead><TableHead className="text-right">IBS</TableHead>
                    <TableHead className="text-right">Total</TableHead><TableHead>Marco</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.anos.map((a) => (
                      <TableRow key={a.ano}>
                        <TableCell className="font-medium">{a.ano}</TableCell>
                        <Din v={a.icmsIss} /><Din v={a.pisCofins} /><Din v={a.ipi} /><Din v={a.cbs} /><Din v={a.ibs} />
                        <TableCell className="text-right whitespace-nowrap font-semibold">{formatCurrency(a.total)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground min-w-48">{a.nota ?? ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <p className="text-xs text-muted-foreground mt-2">Cada tributo já descontado dos créditos das compras.</p>
              </div>
            )}
            <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-0.5">
              {data.premissas.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Auxiliares ───────────────────────────────────────────────────────────────

function Kpi({ titulo, valor, detalhe, alerta = false }: { titulo: string; valor: string; detalhe?: string; alerta?: boolean }) {
  return (
    <div className={`rounded-md border p-3 ${alerta ? 'border-amber-500' : ''}`}>
      <p className="text-base sm:text-lg font-semibold break-words">{valor}</p>
      <p className="text-xs text-muted-foreground">{titulo}</p>
      {detalhe && <p className="text-xs text-muted-foreground mt-0.5">{detalhe}</p>}
    </div>
  );
}

const Din = ({ v }: { v: number }) => <TableCell className="text-right whitespace-nowrap">{formatCurrency(v)}</TableCell>;
